import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_DOMAIN } from 'consts';
import { makeApiRequest } from 'helpers';
import map from 'lodash/map';
import reject from 'lodash/reject';
import { useMemo } from 'react';
import { security } from 'shared';

export type ChatSummary = {
  id: string;
  user_id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

type ChatsPage = {
  results: ChatSummary[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
};

const PAGE_SIZE = 100;

export function useChats (search?: string) {
  const query = useInfiniteQuery<ChatsPage>({
    queryKey: ['chats', { q: search }],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(pageParam),
      });
      if (search) params.set('q', search);
      return makeApiRequest(`/api/chats?${params}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
  });

  const chats = useMemo(
    () => query.data?.pages.flatMap((p) => p.results),
    [query.data],
  );

  return { ...query, chats };
}

// Metadata for a single chat (title etc.). The key shares the `['chats']`
// prefix so the rename/create invalidations below refetch it too.
export function useChatSummary (id?: string) {
  return useQuery<ChatSummary>({
    queryKey: ['chats', 'detail', id],
    queryFn: () => makeApiRequest(`/api/chats/${id}`),
    enabled: !!id,
  });
}

export function useRenameChat () {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const token = await security.getAccessTokenSilently()();
      const resp = await fetch(`${API_DOMAIN}/api/chats/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title }),
      });
      if (!resp.ok) throw new Error(`Failed to rename chat: ${resp.status}`);
      return resp.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });
}

export function useDeleteChat () {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await security.getAccessTokenSilently()();
      const resp = await fetch(`${API_DOMAIN}/api/chats/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Failed to delete chat: ${resp.status}`);
      return resp.json();
    },
    // Optimistic removal from cache — iterate pages to find and remove the chat
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['chats'] });
      const queryCache = queryClient.getQueriesData<{ pages: ChatsPage[]; pageParams: number[] }>({ queryKey: ['chats'] });

      for (const [key, data] of queryCache) {
        if (!data?.pages) continue;
        queryClient.setQueryData(key, {
          ...data,
          pages: map(data.pages, (page) => ({
            ...page,
            results: reject(page.results, { id }),
          })),
        });
      }

      return { queryCache };
    },
    onError: (_err, _id, context) => {
      // Restore all cached query data on error
      if (context?.queryCache) {
        for (const [key, data] of context.queryCache) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });
}
